<template>
  <div class="relative">
    <!-- Mobile view - flex wrap without swiper -->
    <RadioGroup
      :model-value="selectedCategory"
      class="flex md:hidden flex-row flex-wrap gap-2 items-center"
    >
      <!-- "All" Category for mobile -->
      <RadioGroupOption
        :value="''"
        class="flex cursor-pointer"
        @click="handleCategoryChange('')"
      >
        <RadioGroupLabel
          :class="
            isSelected('') ? 'bg-complementary-600 !border-grays-800' : ''
          "
          class="w-fit h-fit rounded-[4px] border border-grays-300 py-[3px] px-4 group hover:border-grays-600 focus:border-grays-800 active:border-grays-800 cursor-pointer"
        >
          <p
            :class="isSelected('') ? '!text-complementary-900' : ''"
            class="text-base text-grays-800 font-medium group-hover:text-grays-900 group-focus:text-grays-900 group-active:text-grays-900"
          >
            הכל
          </p>
        </RadioGroupLabel>
      </RadioGroupOption>

      <!-- Dynamic Categories for mobile -->
      <RadioGroupOption
        v-for="category in categoriesList"
        :key="category.name"
        :value="category.name"
        class="flex cursor-pointer"
        @click="handleCategoryChange(category.name)"
      >
        <RadioGroupLabel
          :class="[
            'w-fit h-fit rounded-[4px] border border-grays-300 py-[3px] px-4 group',
            isSelected(category.name)
              ? 'bg-complementary-600 !border-grays-800'
              : '',
            'hover:border-grays-600 focus:border-grays-800 active:border-grays-800 cursor-pointer',
          ]"
        >
          <p
            :class="[
              'text-base font-medium group-hover:text-grays-900',
              isSelected(category.name)
                ? '!text-complementary-900'
                : 'text-grays-800',
            ]"
          >
            {{ category.name }}
          </p>
        </RadioGroupLabel>
      </RadioGroupOption>
    </RadioGroup>

    <!-- Desktop view - original swiper -->
    <RadioGroup
      :model-value="selectedCategory"
      class="hidden md:flex flex-row flex-nowrap gap-4 items-center"
    >
      <!-- Custom Navigation Buttons -->
      <button
        :class="{ 'inactive-arrow': atEnd }"
        class="arrow-left custom-swiper-button material-symbols-rounded"
      >
        chevron_left
      </button>
      <button
        :class="{ 'inactive-arrow': atBeginning }"
        class="arrow-right custom-swiper-button material-symbols-rounded"
      >
        chevron_right
      </button>
      <swiper
        ref="swiperRef"
        :navigation="{
          nextEl: '.arrow-left',
          prevEl: '.arrow-right',
        }"
        :slides-per-view="'auto'"
        :space-between="5"
        class="flex"
        @fromEdge="resetArrows"
        @reachBeginning="atBeginning = true"
        @reachEnd="atEnd = true"
      >
        <!-- "All" Category -->
        <swiper-slide class="w-fit text-nowrap flex">
          <RadioGroupOption
            :value="''"
            class="flex cursor-pointer"
            id="all-category-option"
            @click="handleCategoryChange('')"
          >
            <RadioGroupLabel
              :class="
                isSelected('') ? 'bg-complementary-600 !border-grays-800' : ''
              "
              class="w-fit h-fit rounded-[4px] border border-grays-300 py-[3px] px-4 group hover:border-grays-600 focus:border-grays-800 active:border-grays-800 cursor-pointer"
            >
              <p
                :class="isSelected('') ? '!text-complementary-900' : ''"
                class="text-base text-grays-800 font-medium group-hover:text-grays-900 group-focus:text-grays-900 group-active:text-grays-900"
              >
                הכל
              </p>
            </RadioGroupLabel>
          </RadioGroupOption>
        </swiper-slide>

        <!-- Dynamic Categories -->
        <swiper-slide
          v-for="category in categoriesList"
          :key="category.name"
          class="w-fit text-nowrap flex"
        >
          <RadioGroupOption
            :value="category.name"
            class="flex cursor-pointer"
            @click="handleCategoryChange(category.name)"
          >
            <RadioGroupLabel
              :class="[
                'w-fit h-fit rounded-[4px] border border-grays-300 py-[3px] px-4 group',
                isSelected(category.name)
                  ? 'bg-complementary-600 !border-grays-800'
                  : '',
                'hover:border-grays-600 focus:border-grays-800 active:border-grays-800 cursor-pointer',
              ]"
            >
              <p
                :class="[
                  'text-base font-medium group-hover:text-grays-900',
                  isSelected(category.name)
                    ? '!text-complementary-900'
                    : 'text-grays-800',
                ]"
              >
                {{ category.name }}
              </p>
            </RadioGroupLabel>
          </RadioGroupOption>
        </swiper-slide>
      </swiper>
    </RadioGroup>
  </div>
</template>

<script>
import { Swiper, SwiperSlide } from "swiper/vue";
import SwiperCore, { Navigation } from "swiper";
import { RadioGroup, RadioGroupLabel, RadioGroupOption } from "@headlessui/vue";

SwiperCore.use([Navigation]); // Use only Navigation module

export default {
  components: {
    RadioGroupLabel,
    RadioGroupOption,
    RadioGroup,
    Swiper,
    SwiperSlide,
  },
  props: {
    categoriesList: {
      type: Array,
      default: () => [],
    },
    selectedCategory: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      atBeginning: true,
      atEnd: false,
    };
  },
  methods: {
    handleCategoryChange(category) {
      if (category === "") {
        this.$gtag?.event("filter_all");
      } else {
        this.$gtag?.event("filter_category", { category_name: category });
      }
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
      this.$emit("update:selectedCategory", category);
      this.$emit("selected-category", category);
      this.$nextTick(() => {
        this.scrollToCategory(category);
      });
    },
    scrollToCategory(category) {
      const swiper = this.$refs.swiperRef?.$el?.swiper;
      if (!swiper) return;
      if (category === "") {
        swiper.slideTo(0);
      } else {
        const index = this.categoriesList.findIndex((c) => c.name === category);
        if (index !== -1) {
          swiper.slideTo(index + 1);
        }
      }
    },
    isSelected(category) {
      return this.selectedCategory === category;
    },
    resetArrows() {
      this.atBeginning = false;
      this.atEnd = false;
    },
  },
  watch: {
    categoriesList() {
      this.$nextTick(() => {
        const swiper = this.$refs.swiperRef?.$el?.swiper;
        if (swiper) {
          this.atEnd = swiper.isEnd;
        }
      });
    },
  },
};
</script>

<style>
.custom-swiper-button {
  color: #707070 !important;
  position: absolute;
  z-index: 50;
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
}

.arrow-right {
  right: 0;
}

.arrow-left {
  left: 0;
}

.inactive-arrow {
  opacity: 0.3;
  cursor: not-allowed;
}

/* Hide swiper and show flex categories in mobile view */
@media (max-width: 767px) {
  .swiper {
    display: none;
  }

  .md\\:hidden {
    display: flex !important;
  }
}

/* Show swiper in desktop view */
@media (min-width: 768px) {
  .swiper {
    display: flex !important;
  }

  .md\\:hidden {
    display: none !important;
  }
}
</style>
